/* eslint-disable jsx-a11y/prefer-tag-over-role, jsx-a11y/interactive-supports-focus,
   jsx-a11y/click-events-have-key-events --
   ⚠ Te trzy reguly zakladaja wzorzec, w ktorym pozycje listy same lapia ognisko.
   Combobox z WAI-ARIA dziala odwrotnie: ognisko ZOSTAJE w polu tekstowym przez caly czas,
   a to, ktora pozycja jest aktywna, oglasza `aria-activedescendant`. Nadanie pozycjom
   `tabIndex` albo wlasnej obslugi klawiatury zlamaloby ten wzorzec — czytnik ekranu
   dostalby dwa konkurujace zrodla ogniska. Klawiature obsluguje pole (`onInputKeyDown`).
   `select`/`datalist` odpadaja z innego powodu: lista ma naglowki grup, wlasna tresc
   wierszy i jest sterowana z zewnatrz. */
import { useEffect, useRef } from 'react';

import type { OmniboxRow } from '../model/options';
import { optionDomId, selectableOf } from '../model/options';
import styles from './Omnibox.module.css';

/**
 * Lista omniboksa wg wzorca **combobox** z WAI-ARIA.
 *
 * Ognisko zostaje **w polu tekstowym** przez cały czas — pozycje nie są w kolejności
 * tabulacji, a to, która jest aktywna, ogłasza `aria-activedescendant`. To inny wzorzec
 * niż w drzewku (`tree` + roving tabindex) i różnica nie jest kosmetyczna: tam ognisko
 * wędruje po wierszach, tutaj musi zostać tam, gdzie człowiek pisze.
 */
interface OmniboxListProps {
  id: string;
  rows: readonly OmniboxRow[];
  activeIndex: number;
  onHover: (index: number) => void;
  children: (row: OmniboxRow, state: { active: boolean; id: string }) => React.ReactNode;
}

export function OmniboxList({ id, rows, activeIndex, onHover, children }: OmniboxListProps) {
  const listRef = useRef<HTMLDivElement>(null);
  const selectable = selectableOf(rows);
  const activeKey = selectable[activeIndex]?.key;

  // Przewinięcie do zaznaczonej pozycji. `block: 'nearest'` — lista ma nie skakać,
  // gdy pozycja i tak jest widoczna; przy nawigacji strzałkami przewijanie na środek
  // sprawia, że treść ucieka spod wzroku.
  useEffect(() => {
    if (!activeKey) {
      return;
    }

    listRef.current
      ?.querySelector(`[data-option-key="${CSS.escape(activeKey)}"]`)
      ?.scrollIntoView({ block: 'nearest' });
  }, [activeKey]);

  return (
    <div ref={listRef} id={id} className={styles['list']} role="listbox">
      {rows.map((row) => {
        if (row.kind === 'heading') {
          return (
            <div key={row.key} className={styles['groupHeading']} role="presentation">
              {row.label}
            </div>
          );
        }

        const index = selectable.indexOf(row);
        const active = index === activeIndex;

        return (
          <div
            key={row.key}
            id={optionDomId(id, row.key)}
            data-option-key={row.key}
            className={`${styles['item']} ${active ? styles['itemActive'] : ''}`}
            role="option"
            aria-selected={active}
            aria-disabled={row.disabled ?? false}
            // Wskaźnik tylko podświetla; wykonanie zostaje na kliknięciu, żeby
            // przypadkowe przesunięcie myszy nie zmieniało tego, co zrobi Enter.
            onMouseMove={() => onHover(index)}
            onClick={() => {
              if (!row.disabled) {
                row.onSelect();
              }
            }}
          >
            {children(row, { active, id: optionDomId(id, row.key) })}
          </div>
        );
      })}
    </div>
  );
}
